import React, { useEffect, useRef, useState } from "react";
import { Alert, Badge, Table } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import apis from "../../services";

const money = (minor) => (Number(minor || 0) / 100).toFixed(2);
const normalizeQuote = (quote) => quote ? {
  ...quote,
  amountInvestedMinor: quote.amountInvestedMinor ?? Math.round(Number(quote.amountInvested || 0) * 100),
  dividendRemainderMinor: quote.dividendRemainderMinor ?? Math.round(Number(quote.dividendRemainder || 0) * 100),
  allocations: (quote.allocations || quote.lotAllocations || []).map((allocation) => ({
    ...allocation,
    priceMinor: allocation.priceMinor ?? Math.round(Number(allocation.pricePerShare || 0) * 100),
    subtotalMinor: allocation.subtotalMinor ?? Math.round(Number(allocation.subtotal || 0) * 100),
  })),
} : null;

const LotAllocationPreview = ({ amount, onQuote }) => {
  const { i18n } = useTranslation();
  const spanish = String(i18n.language || "").startsWith("sp") || String(i18n.language || "").startsWith("es");
  const numericAmount = Number(amount || 0);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const onQuoteRef = useRef(onQuote);

  useEffect(() => { onQuoteRef.current = onQuote; }, [onQuote]);

  useEffect(() => {
    if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
      setQuote(null);
      setError("");
      onQuoteRef.current?.(null);
      return undefined;
    }
    let active = true;
    const timer = setTimeout(async () => {
      setLoading(true);
      setError("");
      try {
        const response = await apis.quoteSharePurchase({ amount: numericAmount });
        if (active) {
          const nextQuote = normalizeQuote(response?.data?.quote);
          setQuote(nextQuote);
          onQuoteRef.current?.(nextQuote);
        }
      } catch (requestError) {
        if (active) {
          setQuote(null);
          onQuoteRef.current?.(null);
          setError(requestError?.message || "Unable to calculate the lot quote");
        }
      } finally {
        if (active) setLoading(false);
      }
    }, 250);
    return () => { active = false; clearTimeout(timer); };
  }, [numericAmount]);

  if (numericAmount <= 0) return null;

  return (
    <div className="border rounded p-3 mt-3 bg-white">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>{spanish ? "Asignación por lote" : "Share lot allocation"}</strong>
        {quote && <Badge bg="danger">{quote.shareQuantity} {spanish ? "acciones" : "shares"}</Badge>}
      </div>
      {loading && <small className="text-muted">{spanish ? "Calculando cotización en vivo…" : "Calculating live quote…"}</small>}
      {error && <Alert variant="warning" className="py-2 mb-0">{error}</Alert>}
      {quote && (
        <>
          {quote.allocations?.length > 1 && (
            <Alert variant="warning" className="py-2">
              {spanish
                ? "La inversión cruza el límite de 100,000 acciones. El siguiente lote usa el precio incrementado."
                : "This investment crosses the 100,000-share boundary. The next lot uses the increased price."}
            </Alert>
          )}
          <Table responsive bordered size="sm" className="mb-2">
            <thead><tr><th>{spanish ? "Lote" : "Lot"}</th><th>{spanish ? "Acciones" : "Shares"}</th><th>{spanish ? "Precio" : "Price"}</th><th>Subtotal</th></tr></thead>
            <tbody>
              {(quote.allocations || []).map((item) => (
                <tr key={`${item.lotCode}-${item.priceMinor}`}>
                  <td><strong>{item.lotCode}</strong></td>
                  <td>{item.quantity}</td>
                  <td>${money(item.priceMinor)} MXN</td>
                  <td>${money(item.subtotalMinor)} MXN</td>
                </tr>
              ))}
            </tbody>
          </Table>
          <small className="text-muted">
            {spanish
              ? "Los folios permanentes se asignan después de confirmar la inversión."
              : "Permanent folio IDs are assigned after the investment is confirmed."}
          </small>
        </>
      )}
    </div>
  );
};

export default LotAllocationPreview;
